import { useState } from 'react'
import { Ruler, LayoutGrid, EyeOff } from 'lucide-react'
import { useTools } from '../../ToolProvider.jsx'
import InputSpinner2 from '../ToolSelectorRight/components/InputSpinner2/InputSpinner2.jsx'

const MODES = [
    { value: 'conjunto', label: 'Conjunto', title: 'Una cota para todo el fondo', Icon: Ruler },
    { value: 'piezas', label: 'Piezas', title: 'Una cota por cada pieza suelta del fondo', Icon: LayoutGrid },
    { value: 'ninguna', label: 'Ocultar', title: 'Sin cotas en el lienzo', Icon: EyeOff },
]

// Piezas sueltas del fondo (p. ej. letras que no se tocan tras un
// Fill/Offset de dibujos separados): el modo "conjunto" (una sola cota,
// como hasta ahora) sigue siendo el que manda el ancho/alto de
// Base/Cuerpo — este dock solo añade "por piezas" (ver y redimensionar
// cada una) y "ocultar" (cuando las cotas estorban).
const CotasDock = () => {
    const { onSetCotaDisplayMode, getCotaDisplayMode, getBackgroundPieces, onResizeBackgroundPiece } = useTools()
    const [mode, setModeState] = useState(() => getCotaDisplayMode())
    const [pieces, setPieces] = useState([])
    const [selectedIndex, setSelectedIndex] = useState(null)

    const refreshPieces = () => {
        const next = getBackgroundPieces()
        setPieces(next)
        setSelectedIndex((current) => (current !== null && next[current] ? current : (next.length ? 0 : null)))
    }

    const handleModeChange = (nextMode) => {
        setModeState(nextMode)
        onSetCotaDisplayMode(nextMode)
        if (nextMode === 'piezas') refreshPieces()
    }

    const selectedPiece = selectedIndex !== null ? pieces[selectedIndex] : null

    const handleResize = (axis, value) => {
        if (!selectedPiece) return
        const width = axis === 'width' ? value : selectedPiece.widthMm
        const height = axis === 'height' ? value : selectedPiece.heightMm
        const applied = onResizeBackgroundPiece(selectedPiece.index, width, height)
        if (applied) refreshPieces()
    }

    return (
        <div className="dropdown dropdown-top dropdown-end">
            <label
                tabIndex="0"
                className="btn btn-sm btn-circle btn-ghost tooltip tooltip-top cursor-pointer"
                data-tip="Cotas del fondo"
                aria-label="Cotas del fondo"
                onClick={() => { if (mode === 'piezas') refreshPieces() }}
            >
                <Ruler className="h-[1.1rem] w-[1.1rem]" />
            </label>
            <div tabIndex="0" className="dropdown-content menu p-3 shadow bg-base-100 rounded-box w-64 z-50 gap-2">
                <div className="join">
                    {MODES.map(({ value, label, title, Icon }) => (
                        <button
                            key={value}
                            type="button"
                            title={title}
                            className={`join-item btn btn-sm flex-1 gap-1 ${mode === value ? 'btn-primary' : 'btn-ghost border border-base-300'}`}
                            onClick={() => handleModeChange(value)}
                        >
                            <Icon className="w-4 h-4" />
                            {label}
                        </button>
                    ))}
                </div>

                {mode === 'piezas' && (
                    <>
                        <div className="divider my-1" />
                        {pieces.length === 0 ? (
                            <p className="text-xs text-base-content/60">No hay piezas sueltas en el fondo.</p>
                        ) : (
                            <>
                                <div className="flex flex-wrap gap-1">
                                    {pieces.map((piece) => (
                                        <button
                                            key={piece.index}
                                            type="button"
                                            className={`btn btn-xs ${selectedIndex === piece.index ? 'btn-primary' : 'btn-ghost border border-base-300'}`}
                                            onClick={() => setSelectedIndex(piece.index)}
                                        >
                                            Pieza {piece.index + 1}
                                        </button>
                                    ))}
                                </div>
                                {selectedPiece && (
                                    <div className="flex gap-2 items-end mt-2">
                                        <div className="flex-1 flex flex-col">
                                            <span className="text-xs">Ancho (mm)</span>
                                            <InputSpinner2
                                                value={selectedPiece.widthMm}
                                                onChange={(value) => handleResize('width', value)}
                                                min={1}
                                                max={1000}
                                                initialStep={1}
                                            />
                                        </div>
                                        <div className="flex-1 flex flex-col">
                                            <span className="text-xs">Alto (mm)</span>
                                            <InputSpinner2
                                                value={selectedPiece.heightMm}
                                                onChange={(value) => handleResize('height', value)}
                                                min={1}
                                                max={1000}
                                                initialStep={1}
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default CotasDock
